import { useEffect, useState } from 'react';
import {
  Autocomplete,
  IconDown,
  IconLoader,
  IconTrash,
  IconUp,
  OnSelectChange,
  Select,
  SelectSize,
  SelectType,
  Textarea,
} from '@ui/src';
import {
  ExpectedFundsOriginsFormValues,
  NewApplicationStatus,
} from '../../../../hoc/new-application/types';
import { ExpectedFundsOriginsContentTypes } from '../types';
import InfoRow from '../info-row';

import cn from '../styles.module.css';

const ExpectedFundsOriginsContent = ({
  status,
  isEditable,
  currencies,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOrigin,
  setExpectedFundsOriginsValues,
  expectedFundsOrigins,
  index,
  countries,
}: ExpectedFundsOriginsContentTypes) => {
  const [formValues, setFormValues] =
    useState<ExpectedFundsOriginsFormValues>();
  const [expanded, setExpanded] = useState(true);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    const nextValues = expectedFundsOrigins?.map((item) => {
      const result = { ...item };
      const nextFormValues = { ...formValues };

      if (nextFormValues.id === item.id) {
        if (e.target.name === `currencyId-${item.id}`) {
          nextFormValues.currencyId = e.target.value as string;
          result.currencyId = e.target.value as string;
        } else if (
          e.target.name === `mainCounterpartiesAndWebsites-${item.id}`
        ) {
          nextFormValues.mainCounterpartiesAndWebsites = e.target
            .value as string;
          result.mainCounterpartiesAndWebsites = e.target.value as string;
        } else if (e.target.name === `countryOfIncorporationId-${item.id}`) {
          nextFormValues.countryOfIncorporationId = e.target.value as string;
          result.countryOfIncorporationId = e.target.value as string;
        } else if (e.target.name === `counterpartyBankCountryIds-${item.id}`) {
          nextFormValues.counterpartyBankCountryIds = e.target
            .value as string[];
          result.counterpartyBankCountryIds = e.target.value as string[];
        } else if (e.target.name === `activityCountryIds-${item.id}`) {
          nextFormValues.activityCountryIds = e.target.value as string[];
          result.activityCountryIds = e.target.value as string[];
        } else if (e.target.name === `mainActivities-${item.id}`) {
          nextFormValues.mainActivities = e.target.value as string;
          result.mainActivities = e.target.value as string;
        } else if (e.target.name === `transactionReason-${item.id}`) {
          nextFormValues.transactionReason = e.target.value as string;
          result.transactionReason = e.target.value as string;
        }

        setFormValues(nextFormValues as ExpectedFundsOriginsFormValues);
      }

      return result;
    });

    setExpectedFundsOriginsValues(
      nextValues as ExpectedFundsOriginsFormValues[],
    );
  };

  useEffect(() => {
    if (expectedFundsOrigin) {
      setFormValues(expectedFundsOrigin);
    }
  }, [expectedFundsOrigin]);

  return (
    <div
      className={`${cn.accountCurrencyWrapper} ${!expanded ? cn.collapsed : ''}`}
    >
      <div
        className={`${cn.accountCurrencyTopWrapper} ${!expanded ? cn.collapsed : ''}`}
      >
        <div className={cn.accountCurrencyTop}>
          <button
            type="button"
            className={cn.accountCurrencyTopLeft}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <IconUp width={14} height={14} />
            ) : (
              <IconDown height={14} width={14} />
            )}
            {`Counterparties #${index + 1}`}
          </button>
          {isEditable &&
            status !== NewApplicationStatus.ACCEPTED &&
            status !== NewApplicationStatus.DECLINED && (
              <button
                type="button"
                className={cn.deleteAccountCurrencyButton}
                onClick={() =>
                  onDeleteExpectedFundsOrigins(
                    expectedFundsOrigin?.id as string,
                  )
                }
              >
                {deleteExpectedFundsOriginsInProgress &&
                selectedExpectedFundsOriginsId === expectedFundsOrigin?.id ? (
                  <IconLoader width={18} height={18} />
                ) : (
                  <IconTrash />
                )}
              </button>
            )}
        </div>
      </div>
      {expanded && (
        <>
          <InfoRow title="Account currency" lessPadding>
            <Select
              name={`currencyId-${expectedFundsOrigin?.id}`}
              placeholder="Select currency..."
              list={currencies}
              value={formValues?.currencyId}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              borderless
              fullWidth
            />
          </InfoRow>
          <InfoRow
            title="Names of main counterparties + website"
            className={cn.noFlex}
            lessPadding
          >
            <Textarea
              name={`mainCounterpartiesAndWebsites-${expectedFundsOrigin?.id}`}
              placeholder="Describe the names..."
              value={formValues?.mainCounterpartiesAndWebsites}
              onChange={onChange}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          </InfoRow>
          <InfoRow
            title="Country of incorporation"
            style={{
              position: 'relative',
              zIndex: 10,
            }}

            lessPadding
          >
            <Autocomplete
              name={`countryOfIncorporationId-${expectedFundsOrigin?.id}`}
              placeholder="Select country..."
              list={countries}
              value={formValues?.countryOfIncorporationId}
              size={SelectSize.SMALL}
              onChange={(e) => {
                onChange({
                  ...e,
                  target: {
                    ...e.target,
                    name: `countryOfIncorporationId-${expectedFundsOrigin?.id}`,
                  },
                });
              }}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              borderless
              fullWidth
            />
          </InfoRow>
          <InfoRow
            title="Countries of counterparty's banks"
            style={{
              position: 'relative',
              zIndex: 9,
            }}
            lessPadding
          >
            <Select
              name={`counterpartyBankCountryIds-${expectedFundsOrigin?.id}`}
              placeholder="Select countries..."
              list={countries}
              value={formValues?.counterpartyBankCountryIds}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              mode={SelectType.MULTIPLE}
              borderless
              fullWidth
            />
          </InfoRow>
          <InfoRow
            title="Countries of activities"
            style={{
              position: 'relative',
              zIndex: 8,
            }}
            lessPadding
          >
            <Select
              name={`activityCountryIds-${expectedFundsOrigin?.id}`}
              placeholder="Select countries..."
              list={countries}
              value={formValues?.activityCountryIds}
              size={SelectSize.SMALL}
              onChange={onChange}
              selectWrapperClassName={
                status === NewApplicationStatus.ACCEPTED ||
                status === NewApplicationStatus.DECLINED
                  ? cn.selectDisabled
                  : ''
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              mode={SelectType.MULTIPLE}
              borderless
              fullWidth
            />
          </InfoRow>
          <InfoRow title="Main activities" className={cn.noFlex} lessPadding>
            <Textarea
              name={`mainActivities-${expectedFundsOrigin?.id}`}
              placeholder="Enter description..."
              value={formValues?.mainActivities}
              onChange={onChange}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          </InfoRow>
          <InfoRow
            title="Reason for transaction with the Customer"
            className={cn.noFlex}
            lessPadding
          >
            <Textarea
              name={`transactionReason-${expectedFundsOrigin?.id}`}
              placeholder="Enter description..."
              value={formValues?.transactionReason}
              onChange={onChange}
              className={`borderless ${cn.pepTextarea} ${cn.stepOneTextarea}`}
              tabIndex={
                !isEditable &&
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
                  ? -1
                  : 0
              }
              disabled={
                !isEditable ||
                Boolean(
                  status === NewApplicationStatus.ACCEPTED ||
                  status === NewApplicationStatus.DECLINED,
                )
              }
            />
          </InfoRow>
        </>
      )}
    </div>
  );
};

export default ExpectedFundsOriginsContent;
